import { useClubSlug } from 'components/Clubs/hooks';
import { useRefetchContext } from 'components/SpaceBooking/SpaceTable/RefetchContext';
import { useModal } from 'hooks/useModal';
import moment from 'moment';
import React, { CSSProperties, useCallback, useMemo } from 'react';
import { BlockingCell, SpaceCellProps } from '.';
import { SpaceBlockingForm, SpaceBookingAssistent } from '..';

const SpacePlaceholder: React.FC<React.PropsWithChildren<React.PropsWithChildren<{ isClosed: boolean }>>> = ({ isClosed }) => {
    if (isClosed) return null;

    return (
        <div
            className="tw-absolute tw-bottom-1/2 tw-right-1/2 tw-translate-x-1/2 tw-translate-y-1/2 tw-text-[0.45rem] tw-text-[rgb(200,200,200)]"
            style={{ writingMode: 'horizontal-tb' }}
        >
            FREI
        </div>
    );
};
const StyledSpaceCell: React.FC<React.PropsWithChildren<React.PropsWithChildren<{
    isClosed: boolean;
    style?: CSSProperties;
    onClick: () => void;
    children?: React.ReactNode;
}>>> = ({ isClosed, style, onClick, children }) => {
    const stateClasses = isClosed ? 'tw-bg-[rgb(228,228,228)]' : 'tw-cursor-pointer hover:tw-bg-[rgb(228,228,228)]';

    return (
        <div
            className={`tw-relative tw-h-full tw-w-full tw-transition-[border] tw-duration-200 ${stateClasses}`}
            style={style}
            onClick={onClick}
        >
            {children}
        </div>
    );
};

export interface Props extends SpaceCellProps {}

export const VerticalSpaceCell = ({
    column,
    cellData,
    rowData,
    rowIndex,
    selectedDate,
    spaceGroup,
    isOwner = false
}: Props) => {
    const startTime = rowIndex;
    const startDate = selectedDate.clone().isoWeekday(column.dayIndex + 1);
    const space = column.space;

    const currentDate = moment();
    const clubSlug = useClubSlug();
    const [setModal, resetModal] = useModal();
    const currentTimeIndex = moment().hours() * 4 + Math.floor(moment().minutes() / 15);
    const { refetchAll } = useRefetchContext();

    const blocking = useMemo(() => cellData?.[startTime], [cellData, startTime]);

    const isCurrentTime = useMemo(
        () => rowIndex - 1 === currentTimeIndex && currentDate.isSame(startDate, 'day'),
        [currentTimeIndex, currentDate, startDate, rowIndex]
    );

    const isClosed = useMemo(() => {
        const openingHours = column.space.opening_hours?.[column.dayIndex];
        return (
            (startDate?.isBefore(currentDate) && !startDate?.isSame(currentDate, 'day')) ||
            (startDate?.isBefore(currentDate) && startTime <= currentTimeIndex) ||
            (openingHours?.start !== null && openingHours?.start > startTime) ||
            (openingHours?.end !== null && openingHours?.end <= startTime)
        );
    }, [column, currentDate, startDate, startTime, rowData, currentTimeIndex]);

    const handleCreateBlocking = useCallback(() => {
        if ((isOwner || !isClosed) && !blocking)
            setModal({
                title: 'Sperrung erstellen',
                content: (
                    <SpaceBlockingForm
                        space={space}
                        startDate={startDate}
                        startTime={startTime}
                        clubSlug={clubSlug}
                        callback={() => {
                            resetModal();
                            refetchAll();
                        }}
                    />
                ),
                show: true
            });
    }, [isOwner, isClosed, blocking, space, startDate, startTime, clubSlug, resetModal, refetchAll, setModal]);

    const handleClick = useCallback(() => {
        if ((isOwner || !isClosed) && !blocking) {
            setModal({
                title: 'Platz buchen',
                content: (
                    <SpaceBookingAssistent
                        space={space}
                        spaceGroup={spaceGroup}
                        startDate={startDate}
                        startTime={startTime}
                    />
                ),
                show: true
            });
        }
    }, [isOwner, isClosed, blocking, space, spaceGroup, startDate, startTime, setModal]);

    const spaceCellStyle: CSSProperties = useMemo(() => {
        const topColor = isCurrentTime ? '#38c172' : rowIndex % 4 === 0 ? 'rgb(200, 200, 200)' : 'rgb(238, 238, 238)';

        const rightColor = column.endOfDay ? 'rgb(190, 190, 190)' : 'rgb(238, 238, 238)';

        return {
            borderTopWidth: 1,
            borderTopStyle: 'solid',
            borderTopColor: topColor,
            borderRightWidth: column.endOfDay ? 2 : 1,
            borderRightStyle: 'solid',
            borderRightColor: rightColor
        };
    }, [isCurrentTime, column, rowIndex]);

    return (
        <StyledSpaceCell
            isClosed={isClosed}
            style={spaceCellStyle}
            onClick={() => (!isOwner ? handleClick() : handleCreateBlocking())}
        >
            {blocking && <BlockingCell vertical isOwner={isOwner} blocking={blocking} />}
            {!isOwner && <SpacePlaceholder isClosed={isClosed} />}
        </StyledSpaceCell>
    );
};
